import { Link } from 'react-router-dom';
import type { ArticleCard as ArticleCardPayload } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { formatDate } from '@/lib/format';
import { Badge, Card, CardBody, cn } from '@/components/ui';
import { Icon } from '@/components/shell';
import { ArticleImage } from './ArticleImage';

/**
 * `.ncard` from the supplied design, and the single card every list on the site uses —
 * the homepage's latest grid, a category listing, search results, related stories.
 *
 * One component rather than one per surface: they differ only in size, and a news site
 * whose cards drift apart looks assembled rather than designed.
 */
export function ArticleCard({
  article,
  priority = false,
  className,
}: {
  article: ArticleCardPayload;
  priority?: boolean;
  className?: string;
}) {
  const { locale, t } = useLocale();

  return (
    <Card as="article" interactive className={cn('group', className)}>
      <Link to={article.path} className="flex flex-1 flex-col">
        <div className="overflow-hidden">
          <ArticleImage
            image={article.image}
            alt={article.headline}
            priority={priority}
            sizes="(max-width: 720px) 100vw, (max-width: 1080px) 50vw, 25vw"
            placeholderSeed={article.category.slug}
            className="transition duration-300 group-hover:scale-105"
          />
        </div>
        <CardBody>
          <Badge tone="category" color={article.category.color}>
            {article.category.name}
          </Badge>
          <h3 className="mt-2 text-lg font-bold text-ink transition group-hover:text-red">
            {article.headline}
          </h3>
          <div className="mt-auto flex items-center justify-between gap-3 pt-3.5 text-xs text-muted">
            <span>
              {formatDate(article.publishedAt, locale)} ·{' '}
              {t('meta.readTime', { minutes: article.readingMinutes })}
            </span>
            <span
              aria-hidden="true"
              className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-line text-red"
            >
              <Icon name="chevron-right" size={12} />
            </span>
          </div>
        </CardBody>
      </Link>
    </Card>
  );
}

/** The compact horizontal form used beside a feature and in related rails (`.feat-mini`). */
export function ArticleMiniCard({ article }: { article: ArticleCardPayload }) {
  const { locale } = useLocale();

  return (
    <Link
      to={article.path}
      className="group flex gap-3 border-b border-line py-3.5 last:border-b-0"
    >
      <ArticleImage
        image={article.image}
        alt={article.headline}
        sizes="80px"
        placeholderSeed={article.category.slug}
        className="w-20 shrink-0 rounded-md"
      />
      <div className="min-w-0">
        {/* `h3`, not `h4`: these sit directly under a section's `h2`, and skipping a
            level is a real navigation failure for anyone browsing by headings. */}
        <h3 className="text-base font-bold text-ink transition group-hover:text-red">
          {article.headline}
        </h3>
        <p className="mt-1.5 text-xs text-muted">{formatDate(article.publishedAt, locale)}</p>
      </div>
    </Link>
  );
}
